export interface DemoSong {
	fileName:string;
	title:string;
	composer:string;
	instrument:string;
	description:string;
}

export class AudioPlayer {
	private _songs:DemoSong[] = [
		{
			fileName: "Courtly_Masquing_Ayres_No2_Sigo3_short.mp3",
			title: "Courtly Masquing Ayres No. 2",
			composer: "John Adson",
			instrument: "Tenor Recorder",
			description: "Recorded on a Sigo Tenor Recorder. The song is in 3 sections, the third in a different time signature. The lowest staves are automatically transposed down to the contrabass range."
		},
		{
			fileName: "Nick_First_Play.mp3",
			title: "Concerto No. 2 Mvt. 1 Allegro",
			composer: "Telemann",
			instrument: "Violin",
			description: "A first experience! The musician opened the application for the first time, clicked record, and this is what happened. The violin creates a neat sounding string ensemble with the auto transposition."
		},
		{
			fileName: "Courtly_Masquing_Ayres_No14_Trumpet.mp3",
			title: "Courtly Masquing Ayres No. 14",
			composer: "John Adson",
			instrument: "Trumpet",
			description: "Recorded on a trumpet. The auto transposition gives us the sound of trombones for the middle voices and tuba for the bass."
		},
		{
			fileName: "Angels We Have Heard on High Euphonium_short.mp3",
			title: "Angels We Have Heard on High",
			composer: "",
			instrument: "Euphonium",
			description: "Recorded on a solo euphonium. With this beginner level arrangement, the auto-octave transposition gives us the sound of our flugelhorn, euphonium and tuba ensemble."
		},
		{
			fileName: "Inisheer Waltz_SPG_Fira_RG.mp3",
			title: "Inisheer Waltz",
			composer: "Traditional",
			instrument: "Virtual Ensemble",
			description: "This recording was made in a live online session with musicians from different parts of the world, each playing from home. Each player's contributions are queued to take turns, creating an evolving mix."
		},
		{
			fileName: "Euphonium_Courtly Masking Ayres no. 8_short.mp3",
			title: "Courtly Masquing Ayres No. 8",
			composer: "John Adson",
			instrument: "Euphonium",
			description: "This Rennaissance loop features a solo euphonium. With the auto-transposition, we have the effective of flugelhorns, euphonium and tuba, making a very warm brass ensemble."
		},
		{
			fileName: "LoopCollab_trumpet_test.mp3",
			title: "A Quick Loop",
			composer: "ScoreLoops",
			instrument: "Trumpet",
			description: "This short loop consists of a repeating 2 bar progression. The entire mix was recorded in a little over a minute with the auto transposition giving the effect of a full brass ensemble."
		}
	];

	private _currentIndex:number = 0;
	private _audio:HTMLAudioElement | null = null;
	private _isPlaying:boolean = false;

	// DOM Elements
	private _playBtn:HTMLElement;
	private _playIcon:HTMLElement;
	private _pauseIcon:HTMLElement;
	private _prevBtn:HTMLElement;
	private _nextBtn:HTMLElement;
	private _progressBar:HTMLElement;
	private _progressFill:HTMLElement;
	private _currentTimeLabel:HTMLElement;
	private _durationLabel:HTMLElement;
	private _trackTitle:HTMLElement;
	private _trackDetails:HTMLElement;
	private _storyContent:HTMLElement;
	private _nextTrackLabel:HTMLElement;
	private _nextTrackInfo:HTMLElement;
	private _nowPlayingContainer:HTMLElement;
	private _isFirstLoad:boolean = true;

	constructor(){
		if (this._initDOM()){
			this._loadTrack(0);
			this._bindEvents();
		}
	}

	public stop():void {
		this._pause();
	}

	private _initDOM():boolean {
		let root = document.querySelector("#stscr-audioDemosSection");
		if (root == null){
			return false;
		}

		this._playBtn = root.querySelector("#stscr-ad-playBtn") as HTMLElement;
		this._playIcon = root.querySelector("#play-icon") as HTMLElement;
		this._pauseIcon = root.querySelector("#pause-icon") as HTMLElement;
		this._prevBtn = root.querySelector("#stscr-ad-prevBtn") as HTMLElement;
		this._nextBtn = root.querySelector("#stscr-ad-nextBtn") as HTMLElement;
		this._progressBar = root.querySelector(
			"#stscr-ad-progressBar") as HTMLElement;
		this._progressFill = root.querySelector(
			"#stscr-ad-progressFill") as HTMLElement;
		this._currentTimeLabel = root.querySelector(
			"#stscr-ad-currentTime") as HTMLElement;
		this._durationLabel = root.querySelector(
			"#stscr-ad-duration") as HTMLElement;
		this._trackTitle = root.querySelector(
			"#stscr-ad-trackTitle") as HTMLElement;
		this._trackDetails = root.querySelector(
			"#stscr-ad-trackDetails") as HTMLElement;
		this._storyContent = root.querySelector(
			"#stscr-ad-storyContent") as HTMLElement;
		this._nextTrackLabel = root.querySelector(
			"#stscr-ad-nextTrackTitle") as HTMLElement;
		this._nextTrackInfo = root.querySelector(
			".stscr-ad-nextTrackInfo") as HTMLElement;
		this._nowPlayingContainer = root.querySelector(
			".stscr-ad-nowPlaying") as HTMLElement;
		return true;
	}


	private _loadTrack(index:number):void {
		this._currentIndex = index;
		let song = this._songs[index];

		if (this._audio == null){
			this._audio = new Audio();
		}

		this._audio.src = `/LoopCollab/sounds/demos/${song.fileName}`;
		this._audio.load();

		let updateUI = ():void => {
			this._trackTitle.innerText = song.title;
			this._trackDetails.innerText =
				`${song.composer} • ${song.instrument}`;
			this._storyContent.innerHTML = `<p>${song.description}</p>`;

			let nextSong = this._songs[(index + 1) % this._songs.length];
			this._nextTrackLabel.innerText = nextSong.title;

			this._progressFill.style.width = "0%";
			this._currentTimeLabel.innerText = "0:00";
			
			if (!isNaN(this._audio!.duration)) {
				this._durationLabel.innerText = this._formatTime(
					this._audio!.duration);
			}
			else {
				this._durationLabel.innerText = "0:00";
			}
		};

		if (this._isFirstLoad){
			this._isFirstLoad = false;
			updateUI();
		}
		else {
			this._nowPlayingContainer.classList.add("fade-out");
			this._storyContent.classList.add("fade-out");
			this._nextTrackInfo.classList.add("fade-out");

			setTimeout(() => {
				updateUI();
				this._nowPlayingContainer.classList.remove("fade-out");
				this._storyContent.classList.remove("fade-out");
				this._nextTrackInfo.classList.remove("fade-out");
			}, 150);
		}

	}

	private _selectTrack(index:number):void {
		this._loadTrack(index);
		this._play();
	}

	private _bindEvents():void {
		if (this._audio == null){
			return;
		}

		this._playBtn.addEventListener("click", () => this._togglePlay());
		this._prevBtn.addEventListener("click", () => this._prevTrack());
		this._nextBtn.addEventListener("click", () => this._nextTrack());
		this._nextTrackInfo.addEventListener("click", () => {
			this._nextTrack();
		});

		this._audio.addEventListener("loadedmetadata", () => {
			this._durationLabel.innerText =
				this._formatTime(this._audio!.duration);
		});

		this._audio.addEventListener("timeupdate", () => {
			if (this._audio == null){
				return;
			}
			let percent = (this._audio.currentTime /
				this._audio.duration) * 100;
			this._progressFill.style.width = `${percent}%`;
			this._currentTimeLabel.innerText =
				this._formatTime(this._audio.currentTime);
		});

		this._audio.addEventListener("ended", () => {
			this._nextTrack();
		});

		// Seek progress bar click
		this._progressBar.addEventListener("click", (e:MouseEvent) => {
			if (this._audio == null){
				return;
			}
			let rect = this._progressBar.getBoundingClientRect();
			let clickX = e.clientX - rect.left;
			let width = rect.width;
			let percent = clickX / width;
			this._audio.currentTime = percent * this._audio.duration;
		});
	}

	private _togglePlay():void {
		if (this._isPlaying){
			this._pause();
		}
		else {
			this._play();
		}
	}

	private _play():void {
		if (this._audio == null){
			return;
		}
		this._isPlaying = true;
		this._audio.play();

		this._playIcon.style.display = "none";
		this._pauseIcon.style.display = "block";
		this._playBtn.setAttribute("title", "Pause");
	}

	private _pause():void {
		if (this._audio == null){
			return;
		}
		this._isPlaying = false;
		this._audio.pause();

		this._playIcon.style.display = "block";
		this._pauseIcon.style.display = "none";
		this._playBtn.setAttribute("title", "Play");
	}

	private _prevTrack():void {
		let prevIndex = this._currentIndex - 1;
		if (prevIndex < 0){
			prevIndex = this._songs.length - 1;
		}
		this._selectTrack(prevIndex);
	}

	private _nextTrack():void {
		let nextIndex = this._currentIndex + 1;
		if (nextIndex >= this._songs.length){
			nextIndex = 0;
		}
		this._selectTrack(nextIndex);
	}

	private _formatTime(seconds:number):string {
		if (isNaN(seconds)){
			return "0:00";
		}
		let mins = Math.floor(seconds / 60);
		let secs = Math.floor(seconds % 60);
		return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
	}
}
